import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Sparkles } from 'lucide-react';
import { ChatPanel } from '../../components/Chat/ChatPanel';
import { Logo } from '../../components/Logo';
import { requestChatAssistance } from '../../services/ai';
import { AIAssistConversationMessage, ProjectChatMessage } from '../../types';

function createChatMessage(role: ProjectChatMessage['role'], content: string): ProjectChatMessage {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    content,
    createdAt: new Date(),
  };
}

function toConversationHistory(messages: ProjectChatMessage[]): AIAssistConversationMessage[] {
  return messages.map((message) => ({
    role: message.role,
    content: message.content,
  }));
}

export function AssistancePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState<ProjectChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const backTarget = location.state && typeof location.state === 'object' && 'backTarget' in location.state
    ? location.state.backTarget
    : null;
  const backButtonLabel = (
    backTarget
    && typeof backTarget === 'object'
    && typeof backTarget.pathname === 'string'
    && backTarget.pathname === '/admin'
  )
    ? 'Back to admin'
    : 'Back to projects';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'AI Assistance';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const handleSubmit = async (prompt: string) => {
    const priorConversation = toConversationHistory(messages);
    const userMessage = createChatMessage('user', prompt);
    const nextMessages = [...messages, userMessage];

    setMessages(nextMessages);
    setLoading(true);
    setError(null);

    try {
      const summary = await requestChatAssistance(prompt, priorConversation);
      setMessages((currentMessages) => [
        ...currentMessages,
        createChatMessage('assistant', summary),
      ]);
    } catch (err) {
      console.error('Failed to request standalone AI assistance:', err);
      setMessages((currentMessages) => currentMessages.slice(0, -1));
      setError(err instanceof Error ? err.message : 'Failed to get AI assistance');
    } finally {
      setLoading(false);
    }
  };

  function handleBackNavigation() {
    if (
      backTarget
      && typeof backTarget === 'object'
      && typeof backTarget.pathname === 'string'
      && backTarget.pathname
    ) {
      navigate(backTarget.pathname, { state: backTarget.state });
      return;
    }

    navigate('/projects');
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f5f8ff_0%,#ffffff_45%,#eef4ff_100%)]">
      <header className="border-b border-editor-border bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackNavigation}
              className="inline-flex items-center gap-2 rounded-full border border-editor-border bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              {backButtonLabel}
            </button>
            <Logo />
          </div>
          <div className="hidden items-center gap-2 rounded-full border border-[#d9e5ff] bg-[#eef4ff] px-3 py-1.5 text-xs font-medium text-[#25428f] sm:inline-flex">
            <Shield className="h-3.5 w-3.5" />
            Routed through Together AI to Aligned&apos;s Qwen assistant
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <section className="mb-6 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#eef4ff] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#25428f]">
            <Sparkles className="h-3.5 w-3.5" />
            AI Assistance
          </div>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-gray-900">
            AI Assistance
          </h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            This chat is connected to a model owned and operated by Aligned and will not
            pollute the training data for other AI labs.
          </p>
        </section>

        <section className="min-h-[620px] overflow-hidden rounded-[28px] border border-editor-border bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
          <ChatPanel
            title="AI Assistance"
            subtitle="Connected to Aligned's Qwen model on Together AI."
            messages={messages}
            onSubmit={handleSubmit}
            loading={loading}
            error={error}
            onDismissError={() => setError(null)}
            emptyStateEyebrow="Aligned AI"
            emptyStateDescription="Start a conversation with the standalone assistant."
            emptyStateHint="Use this page for general research, drafting, and technical questions without opening a project."
            loadingMessage="Thinking through your question..."
            inputPlaceholder="Ask Aligned AI anything..."
            disabledPlaceholder="AI assistance is currently unavailable"
          />
        </section>
      </main>
    </div>
  );
}
